import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, ShoppingBag, AlertTriangle, Loader2, Minus, Plus, Type, Shapes, LayoutTemplate, Pencil, Bookmark } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, errMsg, eur, fetchBlobUrl } from "@/lib/api";
import { useMeta, inkHex, useFontsTick } from "@/lib/meta";
import { useCart } from "@/lib/cart";
import { computeLayout, initialDesign, unitPrice, basePrice, tierDiscount, unfilledLines, cleanDesign } from "@/lib/layout";
import { StampCanvas } from "@/components/editor/StampCanvas";
import { TextLinesPanel } from "@/components/editor/TextLinesPanel";
import { GraphicsPanel } from "@/components/editor/GraphicsPanel";
import { TemplatesPanel } from "@/components/editor/TemplatesPanel";
import { AccessoryPage } from "@/components/AccessoryPage";
import { ProductGallery } from "@/components/ProductGallery";
import { SizeText, RichDescription } from "@/components/RichText";
import { sizeLabel } from "@/components/ProductThumb";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Summary({ product, design, layout, ink, setInk, qty, setQty, warnings, editKey, pads, setPads }) {
  const meta = useMeta();
  const cart = useCart();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unfilled, setUnfilled] = useState(null);
  const missing = unfilledLines(design);
  const finalDesign = missing.length ? cleanDesign(design) : design;
  const finalLayout = missing.length ? computeLayout(product, finalDesign) : layout;
  const isEmpty = !finalDesign.lines.length && !design.graphics.length;
  const price = unitPrice(product, design, qty);
  const base = basePrice(product, design);
  const tiers = product.config.qty_tiers || [];
  const pct = tierDiscount(tiers, qty);
  const padPrice = product.config.pad_price || 0;
  const inkObj = meta?.inks.find((i) => i.id === ink);

  const preview = async () => {
    setBusy(true);
    try {
      if (isEmpty) return toast.error("Il timbro è vuoto: scrivi almeno una riga");
      setPdfUrl(await fetchBlobUrl({ url: "/render/preview-pdf", method: "post", data: { product_id: product.id, ink_color: ink, layout: finalLayout } }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const addToCart = (confirmed = false) => {
    if (isEmpty) return toast.error("Il timbro è vuoto: scrivi almeno una riga con i tuoi dati");
    if (missing.length && !confirmed) return setUnfilled(missing);
    setUnfilled(null);
    const cp = unitPrice(product, finalDesign, qty), cb = basePrice(product, finalDesign);
    const item = { product_id: product.id, slug: product.slug, name: product.name, quantity: qty, ink_color: ink, unit_price: cp, base_price: cb, tiers, design: finalDesign, layout: finalLayout,
      pads: padPrice > 0 ? pads : 0, pad_price: padPrice, pad_name: product.config.pad_name || "Tampone di ricambio" };
    if (editKey) {
      cart.update(editKey, item);
      toast.success("Timbro aggiornato nel carrello");
      return navigate("/carrello");
    }
    cart.add(item);
    if (window.history.state?.usr?.reorder) return navigate("/carrello", { replace: true });
    toast.success("Timbro aggiunto al carrello", { action: { label: "Vai al carrello", onClick: () => navigate("/carrello") } });
  };

  return (
    <div className="space-y-5" data-testid="summary-panel">
      <div>
        <ProductGallery product={product} />
        <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mt-3">{product.brand}{product.brand && " · "}<mark className="size-mark">{sizeLabel(product)}</mark>{product.sku && <span data-testid="product-sku"> · Cod. {product.sku}</span>}</div>
        <h1 className="text-3xl font-extrabold uppercase leading-tight" data-testid="product-name"><SizeText text={product.name} /></h1>
        <RichDescription html={product.description} className="text-sm text-slate-600 mt-1" testid="product-description" />
      </div>
      {(product.gommino || product.complete) && <SwitchVariant target={product.gommino || product.complete} gommino={!!product.gommino} design={design} ink={ink} qty={qty} />}
      <div>
        <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Colore inchiostro</div>
        <div className="flex flex-wrap gap-2">
          {meta?.inks.filter((i) => product.ink_colors.includes(i.id)).map((i) => (
            <button key={i.id} data-testid={`ink-color-${i.id}`} onClick={() => setInk(i.id)} title={i.name}
              className={`press flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full border text-xs ${ink === i.id ? "border-brand-navy ring-2 ring-brand-navy/10" : "border-slate-200"}`}>
              <span className="w-6 h-6 rounded-full" style={{ background: i.hex }} /> {i.name}
            </button>
          ))}
        </div>
      </div>
      {warnings.length > 0 && (
        <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="overflow-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Alcuni elementi escono dall'area stampabile o si sovrappongono: riduci il carattere, il testo o sposta le grafiche.
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Quantità</span>
          <div className="flex items-center border border-slate-300 rounded-md bg-white h-9">
            <button data-testid="qty-dec-btn" className="w-9 h-full flex items-center justify-center" onClick={() => setQty(Math.max(1, qty - 1))}><Minus className="w-3.5 h-3.5" /></button>
            <span data-testid="qty-value" className="w-8 text-center font-mono text-sm">{qty}</span>
            <button data-testid="qty-inc-btn" className="w-9 h-full flex items-center justify-center" onClick={() => setQty(Math.min(100, qty + 1))}><Plus className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <span className="text-sm text-slate-600">Totale <span className="text-[10px]">(IVA incl.)</span></span>
          <span className="text-right">
            {pct > 0 && <span className="block text-xs text-slate-400 line-through" data-testid="editor-full-price">{eur(base * qty)}</span>}
            <span className="text-3xl font-display font-extrabold" data-testid="editor-total-price">{eur(price * qty + padPrice * pads)}</span>
          </span>
        </div>
        <div className="text-[11px] text-slate-500 text-right" data-testid="editor-unit-price">{eur(price)} cad.{pct > 0 && <span className="ml-1 font-semibold text-green-700">-{pct}%</span>}</div>
        {tiers.length > 0 && (
          <div className="border-t border-slate-200 pt-3" data-testid="qty-tiers">
            <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Più ne ordini, meno spendi</div>
            <div className="grid grid-cols-3 gap-1.5">
              {tiers.map((t) => (
                <button key={t.min_qty} data-testid={`qty-tier-${t.min_qty}`} onClick={() => setQty(t.min_qty)}
                  className={`press rounded-md border px-2 py-1.5 text-center ${pct === t.discount_pct && qty >= t.min_qty ? "border-green-600 bg-green-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  <span className="block text-[11px] text-slate-600">da {t.min_qty} pz</span>
                  <span className="block text-sm font-bold text-green-700">-{t.discount_pct}%</span>
                  <span className="block text-[10px] text-slate-500">{eur(base * (1 - t.discount_pct / 100))}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {padPrice > 0 && (
        <button data-testid="pad-addon-btn" onClick={() => setPads(pads ? 0 : 1)}
          className={`press w-full rounded-lg border-2 p-3 flex items-center gap-3 text-left ${pads ? "border-brand-navy bg-brand-yellow/25" : "border-dashed border-slate-300 bg-white hover:border-brand-navy"}`}>
          <span className="relative w-10 h-10 rounded-md border border-slate-300 bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
            {product.pad?.image ? <img src={product.pad.image} alt="" className="w-full h-full object-contain" /> : <span className="w-7 h-5 rounded-sm" style={{ background: inkObj?.hex }} />}
            {product.pad?.image && <span className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border border-white" style={{ background: inkObj?.hex }} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold">{pads ? "Tampone aggiunto" : "Aggiungi il tampone di ricambio"}</span>
            <span className="block text-[11px] text-slate-600">{product.config.pad_name}{product.pad?.sku && <span className="font-mono"> · Cod. {product.pad.sku}</span>}</span>
            <span className="block text-[11px] font-semibold text-brand-navy" data-testid="pad-addon-color">Colore {inkObj?.name}</span>
          </span>
          <span className="text-sm font-bold whitespace-nowrap" data-testid="pad-addon-price">{pads ? `${pads} × ` : "+"}{eur(padPrice)}</span>
        </button>
      )}
      {padPrice > 0 && pads > 0 && (
        <div className="flex items-center justify-end gap-2 -mt-3 text-xs text-slate-600">
          Quantità tamponi
          <button data-testid="pad-dec-btn" className="w-7 h-7 border rounded flex items-center justify-center" onClick={() => setPads(Math.max(0, pads - 1))}><Minus className="w-3 h-3" /></button>
          <span className="font-mono w-5 text-center" data-testid="pad-qty">{pads}</span>
          <button data-testid="pad-inc-btn" className="w-7 h-7 border rounded flex items-center justify-center" onClick={() => setPads(Math.min(100, pads + 1))}><Plus className="w-3 h-3" /></button>
        </div>
      )}
      <button data-testid="download-pdf-preview-btn" onClick={preview} disabled={busy}
        className="press w-full h-11 rounded-md border border-brand-navy bg-white font-semibold flex items-center justify-center gap-2 hover:bg-brand-navy hover:text-white disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Anteprima PDF in scala 1:1
      </button>
      <button data-testid="add-to-cart-btn" onClick={() => addToCart()}
        className="press w-full h-12 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellowdark">
        {editKey ? <Pencil className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />} {editKey ? "Aggiorna nel carrello" : "Aggiungi al carrello"}
      </button>
      <SaveMine product={product} design={design} layout={layout} ink={ink} />
      <p className="text-[11px] text-slate-500">Controlliamo ogni bozza prima della produzione. Il file definitivo viene generato alle misure esatte della piastra.</p>
      <Dialog open={!!pdfUrl} onOpenChange={(o) => { if (!o) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="pdf-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Anteprima PDF</DialogTitle></DialogHeader>
          {pdfUrl && missing.length > 0 && <p className="text-xs rounded-md bg-amber-50 border border-amber-300 text-amber-900 p-2" data-testid="pdf-unfilled-note">Le righe non compilate (testo di esempio o vuote) non sono incluse nell'anteprima.</p>}
          {pdfUrl && <iframe title="Anteprima PDF" src={pdfUrl} className="flex-1 w-full rounded border" data-testid="pdf-preview-frame" />}
          {pdfUrl && <a href={pdfUrl} download={`anteprima-${product.slug}.pdf`} data-testid="pdf-download-link" className="text-sm font-semibold text-brand-navy hover:underline self-end">Scarica il PDF</a>}
        </DialogContent>
      </Dialog>
      <UnfilledDialog lines={unfilled} onClose={() => setUnfilled(null)} onConfirm={() => addToCart(true)} />
    </div>
  );
}

function SaveMine({ product, design, layout, ink }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);
  const start = () => {
    if (!user) { toast.info("Accedi o registrati per salvare i tuoi timbri"); return navigate(`/accedi?next=${encodeURIComponent(loc.pathname)}`); }
    if (!design.lines.some((l) => l.text.trim() && !l.pristine) && !design.graphics.length) return toast.error("Scrivi prima il testo del timbro");
    setName(loc.state?.savedName || product.name);
  };
  const save = async () => {
    if (!name.trim()) return toast.error("Dai un nome al timbro");
    setBusy(true);
    try {
      const clean = unfilledLines(design).length ? cleanDesign(design) : design;
      const lay = clean === design ? layout : computeLayout(product, clean);
      await api.post("/my/stamps", { name: name.trim(), product_id: product.id, ink_color: ink, design: clean, layout: { border: lay.border, elements: lay.elements } });
      toast.success(`Salvato nei tuoi timbri come "${name.trim()}"`, { action: { label: "Vedi", onClick: () => navigate("/account") } });
      setName(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="save-my-stamp-btn" onClick={start} className="press w-full h-10 rounded-md border border-dashed border-brand-navy text-brand-navy text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellow/20">
        <Bookmark className="w-4 h-4" /> Salva nei miei timbri
      </button>
      <Dialog open={name !== null} onOpenChange={(o) => !o && setName(null)}>
        <DialogContent className="max-w-md" data-testid="save-my-stamp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Salva nei miei timbri</DialogTitle>
            <DialogDescription>Dai un nome per ritrovarlo subito nel tuo account. È visibile solo a te.</DialogDescription>
          </DialogHeader>
          <input autoFocus data-testid="save-my-stamp-name" value={name || ""} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="es. Timbro ufficio, Firma Mario…" className="h-11 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
          <button data-testid="save-my-stamp-confirm" disabled={busy} onClick={save} className="press h-11 rounded-md bg-brand-navy text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />} Salva
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UnfilledDialog({ lines, onClose, onConfirm }) {
  return (
    <Dialog open={!!lines} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-4 border-red-500" data-testid="unfilled-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase text-red-600 flex items-center gap-2"><AlertTriangle className="w-7 h-7" /> Attenzione!</DialogTitle>
          <DialogDescription className="text-slate-700 text-sm">Non hai compilato i seguenti campi del timbro. <b>Verranno cancellati</b> e il timbro sarà riadattato di conseguenza.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5" data-testid="unfilled-list">
          {lines?.map(({ l, i }) => (
            <li key={l.id} className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm" data-testid={`unfilled-line-${i}`}>
              <span className="font-mono text-[11px] text-red-600 font-bold whitespace-nowrap shrink-0">RIGA {i + 1}</span>
              <span className="truncate text-slate-700">{l.text.trim() ? <>testo di esempio: <i>"{l.text}"</i></> : <i>vuota</i>}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button data-testid="unfilled-back-btn" onClick={onClose} className="press flex-1 h-11 rounded-md bg-brand-navy text-white font-semibold">Torna a compilare</button>
          <button data-testid="unfilled-confirm-btn" onClick={onConfirm} className="press flex-1 min-h-11 py-2 px-3 rounded-md border-2 border-red-500 text-red-600 font-semibold hover:bg-red-50">Cancella e aggiungi al carrello</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SwitchVariant({ target, gommino, design, ink, qty }) {
  const navigate = useNavigate();
  return (
    <button data-testid={gommino ? "switch-to-gommino-btn" : "switch-to-complete-btn"}
      onClick={() => navigate(`/timbro/${target.slug}`, { state: { carry: { design, ink_color: ink, quantity: qty } } })}
      className="press w-full flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-brand-navy bg-brand-yellow/15 px-4 py-3 text-left hover:bg-brand-yellow/30">
      <span>
        <span className="block text-sm font-bold text-brand-navy">{gommino ? "Hai già il timbro? Ti serve solo il gommino?" : "Ti serve il timbro completo?"}</span>
        <span className="block text-xs text-slate-600">{gommino ? "Solo la placca in gomma" : "Timbro autoinchiostrante completo"} <mark className="size-mark">{sizeLabel(target)}</mark>{target.sku && ` · Cod. ${target.sku}`} — il tuo testo resta uguale</span>
      </span>
      <span className="shrink-0 text-right"><span className="block font-bold text-brand-navy">{eur(target.price)}</span><span className="text-[11px] font-semibold underline">Passa →</span></span>
    </button>
  );
}

export default function ProductEditor() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const cart = useCart();
  const editItem = cart.items.find((i) => i.key === params.get("modifica") && i.slug === slug);
  const editKey = editItem?.key;
  const locState = useLocation().state;
  const reorder = locState?.reorder;
  const carry = locState?.carry;
  const meta = useMeta();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [design, setDesign] = useState(null);
  const [ink, setInk] = useState("black");
  const [qty, setQty] = useState(1);
  const [pads, setPads] = useState(0);
  const [selected, setSelected] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [tab, setTab] = useState("text");
  const fontsTick = useFontsTick();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const src = editItem || reorder || carry;
      if (src) {
        const d = { ...initialDesign(r.data), ...src.design };
        setDesign(src === carry ? d : { ...d, lines: d.lines.map((l) => ({ ...l, pristine: false })) });
        setInk(r.data.ink_colors.includes(src.ink_color) ? src.ink_color : r.data.ink_colors[0]);
        setQty(src.quantity);
        setPads(src.pads || 0);
      } else {
        setDesign(initialDesign(r.data));
        setInk(r.data.ink_colors[0] || "black");
      }
    }).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, editKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const layout = useMemo(() => (product && design ? computeLayout(product, design) : null), [product, design, fontsTick]);
  if (error) return <div className="max-w-3xl mx-auto p-12 text-center" data-testid="product-error">{error}</div>;
  if (!product || !design || !meta) return <div className="p-12 text-center text-slate-500">Caricamento editor…</div>;
  if (product.kind === "pad") return <AccessoryPage product={product} />;

  const tabBtn = (id, label, Icon) => (
    <button data-testid={`editor-tab-${id}`} onClick={() => setTab(id)}
      className={`press flex-1 h-10 text-sm font-semibold flex items-center justify-center gap-2 border-b-2 ${tab === id ? "border-brand-yellow text-brand-navy" : "border-transparent text-slate-500"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-6 grid gap-5 lg:grid-cols-[400px_1fr] xl:grid-cols-[420px_1fr_340px]" data-testid="product-editor">
      <aside className="min-w-0 order-2 lg:order-1 rounded-xl border border-slate-200 bg-white lg:max-h-[calc(100vh-130px)] lg:overflow-y-auto lg:sticky lg:top-[110px]">
        <div className="flex sticky top-0 bg-white z-10 border-b border-slate-200 px-2">
          {tabBtn("templates", "Modelli", LayoutTemplate)}
          {tabBtn("text", "Testo", Type)}
          {tabBtn("graphics", "Grafica", Shapes)}
        </div>
        <div className="p-4">
          {tab === "templates" ? (
            <TemplatesPanel product={product} design={design} setDesign={(d) => { setDesign(d); }} setSelected={setSelected} ink={inkHex(meta, ink)} fontsTick={fontsTick} />
          ) : tab === "text" ? (
            <TextLinesPanel product={product} design={design} setDesign={setDesign} selected={selected} setSelected={setSelected} warnings={warnings} layout={layout} />
          ) : (
            <GraphicsPanel product={product} design={design} setDesign={setDesign} setSelected={setSelected} />
          )}
        </div>
      </aside>
      <section className="min-w-0 order-1 lg:order-2 lg:sticky lg:top-[110px] self-start">
        {reorder && !editKey && <div className="mb-3 text-xs rounded-md bg-green-50 border border-green-200 text-green-900 p-2.5" data-testid="reorder-banner">{locState?.savedName ? <>Hai aperto <b>{locState.savedName}</b> dai tuoi timbri: modificalo e aggiungilo al carrello</> : "Stai riordinando un timbro: modificalo e aggiungilo al carrello"}</div>}
        {editKey && <div className="lg:hidden mb-3 text-xs rounded-md bg-blue-50 border border-blue-200 text-blue-900 p-2.5">Stai modificando un timbro già nel carrello</div>}
        <StampCanvas product={product} design={design} setDesign={setDesign} layout={layout} ink={inkHex(meta, ink)}
          selected={selected} setSelected={setSelected} onWarnings={setWarnings} />
      </section>
      <aside className="min-w-0 order-3 lg:col-span-2 xl:col-span-1 rounded-xl border border-slate-200 bg-white p-5 self-start xl:sticky xl:top-[110px]">
        {editKey && <div className="mb-4 hidden lg:block text-xs rounded-md bg-blue-50 border border-blue-200 text-blue-900 p-2.5" data-testid="editing-cart-item-banner">Stai modificando un timbro già nel carrello</div>}
        <Summary product={product} design={design} layout={layout} ink={ink} setInk={setInk} qty={qty} setQty={setQty} warnings={warnings} editKey={editKey} pads={pads} setPads={setPads} />
      </aside>
    </div>
  );
}
